import type { ReactNode } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { tonePillClass } from "@/lib/tone";

import type { Secondary, Tile } from "../api";
import type { WindowSummary } from "../window";
import {
  bandTone,
  formatCount,
  formatDeltaRatio,
  formatGbp,
  formatPct,
  formatRatio,
  maturityLabel,
} from "../format";

/**
 * The headline ratio, its trend chart alongside it (W5 - the separate trend
 * Card is gone), and the four figures under it - identical on Overview and on
 * every region, because the same claim rendered two ways is two claims.
 *
 * `window`, when given, is the span the trend chart shows (KET-6591): the ratio
 * and the four figures become that span's totals. The band and "vs prior
 * period" are judged against a whole closed month (the KPI's target grain), so
 * they are shown only for the producer's own month - on any other window they
 * are hidden and the card says why, rather than colouring a 13-week ratio
 * against a monthly target.
 *
 * Every number carries its unit. "899" is not a fact; "GBP 899" is. The one
 * exception is the ratio itself, which states its unit in words beside it
 * ("profit per GBP 1 of ad spend") because "2.41 GBP/GBP" reads as a typo.
 */
export function YieldHeadline({
  tile,
  secondary,
  trend,
  window,
  note,
}: {
  tile: Tile;
  secondary: Secondary;
  trend: ReactNode;
  window?: WindowSummary | null;
  note?: string;
}) {
  const tone = bandTone(tile.band);
  const delta = window ? "" : formatDeltaRatio(tile.delta_vs_prior_period);
  const maturity = maturityLabel(secondary.maturity);
  const value = window ? window.profit_per_pound : tile.value;
  const figures = window
    ? {
        profit: window.profit_gbp,
        sales: window.attributed_sales_gbp,
        spend: window.spend_gbp,
        units: window.attributed_units,
        coverage: window.coverage_pct,
      }
    : {
        profit: secondary.profit_gbp,
        sales: secondary.attributed_sales_gbp,
        spend: secondary.spend_gbp,
        units: secondary.attributed_units,
        coverage: secondary.coverage_pct,
      };
  const periodLabel = window ? window.label : (tile.period ?? "Period pending");

  return (
    <>
      <Card>
        <CardContent className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-1 flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {tile.kpi_name}
            </p>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-4xl font-semibold tabular-nums text-ks-ink">
                {formatRatio(value)}
              </span>
              <span className="text-sm text-muted-foreground">profit per GBP 1 of ad spend</span>
              {!window && (
                <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${tonePillClass(tone)}`}>
                  {tile.band}
                </span>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              Contribution profit{" "}
              <span className="font-semibold tabular-nums text-ks-ink">
                GBP {formatGbp(figures.profit)}
              </span>{" "}
              for {window ? window.label : "the period"}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm">
              {/* An absent delta renders nothing at all. A "0.00" would claim the
                  value held steady, which is not the same as having no prior
                  period to compare it against. */}
              {delta !== "" && (
                <span className={`font-medium tabular-nums ${tonePillClass(tone).split(" ").pop()}`}>
                  {delta} vs prior period
                </span>
              )}
              <span className="text-muted-foreground">{periodLabel}</span>
            </div>

            {window && (
              <p className="text-xs text-muted-foreground">
                Band and change vs prior period are judged on a whole closed month, so they show only
                for {tile.period ?? "the latest closed month"} (Monthly, 30d).
              </p>
            )}
            {note && <p className="text-xs text-muted-foreground">{note}</p>}

            <p className="max-w-prose text-xs text-muted-foreground">
              Attributed contribution profit: attributed sales x contribution margin, minus ad spend.
              Attribution is not incrementality.
            </p>
          </div>

          <div className="w-full lg:max-w-2xl">{trend}</div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Figure label="Attributed sales" value={`GBP ${formatGbp(figures.sales)}`} />
        <Figure label="Ad spend" value={`GBP ${formatGbp(figures.spend)}`} />
        <Figure label="Attributed units" value={formatCount(figures.units)} />
        <Figure
          label="Margin coverage"
          value={formatPct(figures.coverage)}
          note={!window && maturity.show ? maturity.text : undefined}
        />
      </div>
    </>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold tabular-nums text-ks-ink">{value}</p>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
      </CardContent>
    </Card>
  );
}
